import { Fragment, useContext, type ReactNode } from 'react';
import { ButtonsGroup } from '@hungryroot/pho';
import { type BaseTag } from '@hungryroot/rest';
import {
  useApiV1TagGroup as useTagGroup,
  type TagGroups,
} from '@hungryroot/tags';

import {
  ProteinTagTernaryRating,
  type ProteinTagTernaryValue,
} from '../../defs/quiz';
import { QuizContext } from '../../quiz-context';
import { type QuizTernaryTagKeys } from '../../quiz-hooks';
import { proteinPreferenceAnalytics } from '../../segment/quizAnalytics';
import styles from './ControlTagTernaryRating.module.css';

/*
  This is formatted in this way for a reason.
  Referencing computed values at the root of the module
  breaks esbuild/vite tree shaking because it infers
  that code has to run to compute the keys

  See https://esbuild.github.io/api/#tree-shaking-and-side-effects
*/
const buttonGroupOptions = () => {
  return [
    {
      label: 'Absolutely',
      value: ProteinTagTernaryRating.Absolutely,
    },
    {
      label: 'So-so',
      value: ProteinTagTernaryRating.SoSo,
    },
    {
      label: 'No thanks',
      value: ProteinTagTernaryRating.NoThanks,
    },
  ];
};

function updateTernaryTags(
  updateProteinTag: ProteinTagTernaryValue,
  array: ProteinTagTernaryValue[]
): ProteinTagTernaryValue[] {
  const { id: updateProteinTagId } = updateProteinTag;
  const updated = array.reduce(
    (seed, ternaryTag) => {
      const { id } = ternaryTag;
      if (id === updateProteinTagId) {
        return {
          hasMatch: true,
          items: [...seed.items, updateProteinTag],
        };
      }
      return {
        hasMatch: seed.hasMatch,
        items: [...seed.items, ternaryTag],
      };
    },
    {
      hasMatch: false,
      items: [],
    } as {
      hasMatch: boolean;
      items: ProteinTagTernaryValue[];
    }
  );

  const { hasMatch, items } = updated;
  if (hasMatch === true) {
    return items;
  }

  return [...array, updateProteinTag];
}

function getTagRating(
  tagId: number,
  array: ProteinTagTernaryValue[]
): ProteinTagTernaryRating | undefined {
  const match = array.find(({ id }) => {
    return id === tagId;
  });

  return match && match.rated;
}

interface Props<K extends QuizTernaryTagKeys> {
  testid: string;
  tagGroupId: TagGroups;
  question: K;
  filter?: (tag: BaseTag) => boolean;
  children: (params: {
    tag: BaseTag;
    ternaryRating: ReactNode;
    index: number;
  }) => ReactNode;
}

export function ControlTagTernaryRating<K extends QuizTernaryTagKeys>(
  props: Props<K>
) {
  const { testid, tagGroupId, question, children, filter } = props;

  const tagGroup = useTagGroup({ tagGroupId });
  const { data } = tagGroup;
  const { quiz, onQuestionAnswer } = useContext(QuizContext);

  if (quiz === null) {
    return null;
  }

  const { [question]: value } = quiz;
  const normalized: ProteinTagTernaryValue[] = value === null ? [] : value;

  if (data === undefined) {
    return null;
  }

  return (
    <Fragment key="control-tag-ternary-ratings">
      {data.tags[0].filter(filter ?? (() => true)).map((tag, index) => {
        const { id: tagId } = tag;
        const tagCyid = `${testid}-toggle-tag-${tagId}`;
        const ternaryRating = (
          <ButtonsGroup
            className={styles['control-ternary-rating-buttons-group']}
            buttonClassName={styles['control-ternary-rating-button']}
            testid={tagCyid}
            selected={getTagRating(tagId, normalized)}
            handleClick={(value) => {
              const nextCuisines = updateTernaryTags(
                {
                  id: tagId,
                  rated: value,
                },
                normalized
              );

              onQuestionAnswer(quiz, question, nextCuisines);
              proteinPreferenceAnalytics({
                id: tagId,
                name: tag.name,
                preference: value,
              });
            }}
            options={buttonGroupOptions()}
          />
        );
        return children({
          tag,
          ternaryRating,
          index,
        });
      })}
    </Fragment>
  );
}
